import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { queryClient } from 'configs/queryClient';
import { useUserContext } from 'context/UserContextProvider';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useAbortWorkshopDeletionMutation } from 'hooks/mutations/useAbortWorkshopDeletionMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { Trans, useTranslation } from 'react-i18next';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
};

export const AbortDeletion = ({ workshopId }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const abortWorkshopDeletionMutation = useAbortWorkshopDeletionMutation(workshopId);
  const { t } = useTranslation();
  const { user } = useUserContext();
  const hasAbortDeletionPermission = user.permissions.includes('workshops:abort-deletion');
  const toast = useToast(defaultToastOptions);

  return (
    <DangerZoneRow>
      <Stack gap={0}>
        <Heading size="xxs" as="h3">
          {t('workshop:abort_workshop_deletion.title')}
        </Heading>
        <Text> {t('workshop:abort_workshop_deletion.body')}</Text>
      </Stack>
      {hasAbortDeletionPermission ? (
        <Button
          minWidth="24ch"
          colorScheme="red"
          onClick={onOpen}
          isLoading={abortWorkshopDeletionMutation.isPending}
          loadingText={t('workshop:abort_workshop_deletion.button.label')}
          disabled={abortWorkshopDeletionMutation.isPending}
        >
          {t('workshop:abort_workshop_deletion.button.label')}
        </Button>
      ) : (
        <Tooltip label={t('workshop:abort_workshop_deletion.button.tooltip')}>
          <Button minWidth="24ch" colorScheme="red" disabled={!hasAbortDeletionPermission}>
            {t('workshop:abort_workshop_deletion.button.label')}
          </Button>
        </Tooltip>
      )}

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('workshop:abort_workshop_deletion.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Trans i18nKey="workshop:abort_workshop_deletion.modal.body" />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              {t('workshop:abort_workshop_deletion.modal.button_secondary')}
            </Button>
            <Button
              colorScheme="red"
              mr={3}
              onClick={() => {
                abortWorkshopDeletionMutation.mutate(undefined, {
                  onSuccess: () => {
                    onClose();
                    queryClient.invalidateQueries({ queryKey: useWorkshopQuery.getKey(workshopId) });
                    toast({
                      title: t('workshop:abort_workshop_deletion.toast.success.title'),
                      status: 'success',
                    });
                  },
                  onError: (error) => {
                    toast({
                      title: error.message ?? t('common:error'),
                      status: 'error',
                    });
                  },
                });
              }}
              isLoading={abortWorkshopDeletionMutation.isPending}
              loadingText={t('workshop:abort_workshop_deletion.modal.button_primary')}
            >
              {t('workshop:abort_workshop_deletion.modal.button_primary')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DangerZoneRow>
  );
};
